import {makeAutoObservable} from 'mobx';
import {EP_SNAP_SIZES} from '../../../rendering/render.def';

export enum Size {
    '3pt' = 3,
    '4pt' = 4,
    '5pt' = 5,
    '6pt' = 6,
    '7pt' = 7,
    '8pt' = 8,
    '9pt' = 9,
    '10pt' = 10,
    '11pt' = 11,
    '12pt' = 12,
    '13pt' = 13,
    '14pt' = 14,
    '15pt' = 15,
    '16pt' = 16,
    '20pt' = 20,
    '24pt' = 24,
    '28pt' = 28,
    '32pt' = 32,
    '36pt' = 36,
    '40pt' = 40,
}

export enum Font {
    Roboto = 1,
    LibreBaskerville = 2,
    Carlito = 3,
    TimesNewRoman = 4,
    EBGaramond = 5,
    NunitoSans = 6,
    CrimsonText = 7,
    IBMPlexMono = 8,
    OldStandardTT = 9,
    Arial = 10,
    // 11+ are decorative UV-print-only fonts (Google equivalents of the print
    // shop's "School Days" proof sheet). They are excluded from FONT_OPTIONS so
    // the engraved-plate editors are unaffected; UV builds its own option list
    // (UV_FONT_OPTIONS in components/uv-print/uv-fonts.ts).
    GreatVibes = 11, // Snell Roundhand
    Cinzel = 12, // AdornS Serif (Bold Block)
    PlayfairDisplay = 13, // Didot
    PinyonScript = 14, // Ballantines
    Graduate = 15, // Novecento slab condensed (Wish Hand Drawn)
    Pacifico = 16, // Gelato Luxe (Monterey)
    LobsterTwo = 17, // Funkydori (Connecting Script)
    Jost = 18, // Futura medium
    Yellowtail = 19, // CornerStoreJF (Connecting Script)
    Sacramento = 20, // SantElia Script light
    // 21+ are plate-template-only fonts: loaded when a template's HTML names them
    // (loadEPFontsFromHTML), never offered in an editor's font list.
    CormorantGaramond = 21, // INST-PLATES templates (names, degrees)
    Allura = 22, // EP-UPLOAD templates written from the art team's spec PDFs (A-31-04's script opening phrase)
}

export const FONT_FAMILY_MAP: Record<Font, string> = {
    [Font.Roboto]: 'Roboto, Poppins, Open Sans, Proxima Nova, system-ui, sans-serif',
    [Font.LibreBaskerville]: '"Libre Baskerville", Georgia, "Times New Roman", serif',
    [Font.Carlito]: 'Carlito, Calibri, Arial, sans-serif',
    // Tinos / Arimo are the Google-hosted, metric-compatible twins of Times New
    // Roman / Arial. Listing them right after the system font keeps the local
    // appearance identical while guaranteeing the image generator (whose headless
    // browser may lack the Microsoft fonts) lays text out with the SAME metrics —
    // otherwise it falls back to a wider serif and text wraps to an extra line.
    [Font.TimesNewRoman]: '"Times New Roman", Tinos, Times, serif',
    [Font.OldStandardTT]: '"Old Standard TT", Georgia, "Times New Roman", Tinos, serif',
    [Font.Arial]: 'Arial, Arimo, Helvetica, "Liberation Sans", sans-serif',
    [Font.EBGaramond]: '"EB Garamond", Garamond, "Times New Roman", serif',
    [Font.NunitoSans]: '"Nunito Sans", Nunito, system-ui, sans-serif',
    [Font.CrimsonText]: '"Crimson Text", Georgia, "Times New Roman", serif',
    [Font.IBMPlexMono]: '"IBM Plex Mono", Consolas, "Courier New", monospace',
    [Font.GreatVibes]: '"Great Vibes", "Snell Roundhand", cursive',
    [Font.Cinzel]: 'Cinzel, "Times New Roman", serif',
    [Font.PlayfairDisplay]: '"Playfair Display", Didot, "Bodoni MT", serif',
    [Font.PinyonScript]: '"Pinyon Script", cursive',
    [Font.Graduate]: 'Graduate, "Rockwell Condensed", serif',
    [Font.Pacifico]: 'Pacifico, cursive',
    [Font.LobsterTwo]: '"Lobster Two", Lobster, cursive',
    [Font.Jost]: 'Jost, Futura, "Century Gothic", sans-serif',
    [Font.Yellowtail]: 'Yellowtail, cursive',
    [Font.Sacramento]: 'Sacramento, cursive',
    [Font.CormorantGaramond]: '"Cormorant Garamond", Garamond, "Times New Roman", serif',
    [Font.Allura]: 'Allura, cursive',
};

export const FONT_OPTIONS = [
    {value: Font.Roboto, label: 'Roboto'},
    {value: Font.LibreBaskerville, label: 'Libre Baskerville'},
    {value: Font.Carlito, label: 'Carlito'},
    {value: Font.TimesNewRoman, label: 'Times New Roman'},
    {value: Font.OldStandardTT, label: 'Old Standard TT'},
    {value: Font.Arial, label: 'Arial'},
    {value: Font.EBGaramond, label: 'EB Garamond'},
    {value: Font.NunitoSans, label: 'Nunito Sans'},
    {value: Font.CrimsonText, label: 'Crimson Text'},
    {value: Font.IBMPlexMono, label: 'IBM Plex Mono'},
];

export const FONT_SIZES = [
    {value: Size['3pt'], label: Size[Size['3pt']]},
    {value: Size['4pt'], label: Size[Size['4pt']]},
    {value: Size['5pt'], label: Size[Size['5pt']]},
    {value: Size['6pt'], label: Size[Size['6pt']]},
    {value: Size['7pt'], label: Size[Size['7pt']]},
    {value: Size['8pt'], label: Size[Size['8pt']]},
    {value: Size['9pt'], label: Size[Size['9pt']]},
    {value: Size['10pt'], label: Size[Size['10pt']]},
    {value: Size['11pt'], label: Size[Size['11pt']]},
    {value: Size['12pt'], label: Size[Size['12pt']]},
    {value: Size['13pt'], label: Size[Size['13pt']]},
    {value: Size['14pt'], label: Size[Size['14pt']]},
    {value: Size['15pt'], label: Size[Size['15pt']]},
    {value: Size['16pt'], label: Size[Size['16pt']]},
    {value: Size['20pt'], label: Size[Size['20pt']]},
    {value: Size['24pt'], label: Size[Size['24pt']]},
    {value: Size['28pt'], label: Size[Size['28pt']]},
    {value: Size['32pt'], label: Size[Size['32pt']]},
    {value: Size['36pt'], label: Size[Size['36pt']]},
    {value: Size['40pt'], label: Size[Size['40pt']]},
];

export type EPLine = {
    text: string;
    lineHeight: Size;
    size: Size;
    bold: boolean;
    italic: boolean;
};

export const getSnapSizes = (heightIn: number) => {
    const rowDefaultHeight = 0.5;

    if (heightIn <= EP_SNAP_SIZES[0].height + rowDefaultHeight) {
        return EP_SNAP_SIZES[0];
    } else if (heightIn > EP_SNAP_SIZES[0].height + rowDefaultHeight && heightIn <= EP_SNAP_SIZES[1].height + rowDefaultHeight) {
        return EP_SNAP_SIZES[1];
    } else if (heightIn > EP_SNAP_SIZES[1].height + rowDefaultHeight && heightIn <= EP_SNAP_SIZES[2].height + rowDefaultHeight) {
        return EP_SNAP_SIZES[2];
    } else if (heightIn > EP_SNAP_SIZES[2].height + rowDefaultHeight && heightIn <= EP_SNAP_SIZES[4].height + rowDefaultHeight) {
        return EP_SNAP_SIZES[3];
    } else {
        return EP_SNAP_SIZES[4];
    }
};

class EngravedPlateExtendedStore {
    public lines: EPLine[] = [];
    public fontFamily: string;

    constructor() {
        makeAutoObservable(this);
    }
}

export const engravedPlateExtendedStore = new EngravedPlateExtendedStore();

// css2 axis ranges (wght@100..900) only work for variable fonts; a static family such as IBM Plex
// Mono must name its faces, or Google Fonts answers HTTP 400 and the page falls back down the
// family chain (Liberation Mono / Courier New / Consolas). The print PDF bundles exactly the faces
// these requests load (WebNexus Jobs/Orders/ep_pdf/Fonts, see ep-pdf.md "Fonts").
export const GOOGLE_FONT_PARAMS: Partial<Record<Font, string>> = {
    [Font.Roboto]: 'family=Roboto:ital,wght@0,100..900;1,100..900',
    [Font.LibreBaskerville]: 'family=Libre+Baskerville:ital,wght@0,400;0,700;1,400',
    [Font.Carlito]: 'family=Carlito:ital,wght@0,400;0,700;1,400',
    // Metric-compatible web stand-ins for the Microsoft system fonts so they load
    // (and lay out identically) in the headless image generator. See FONT_FAMILY_MAP.
    [Font.TimesNewRoman]: 'family=Tinos:ital,wght@0,400;0,700;1,400',
    [Font.Arial]: 'family=Arimo:ital,wght@0,400;0,700;1,400',
    [Font.OldStandardTT]: 'family=Old+Standard+TT',
    [Font.EBGaramond]: 'family=EB+Garamond:ital,wght@0,400;0,700;1,400',
    [Font.NunitoSans]: 'family=Nunito+Sans:ital,wght@0,200..1000;1,200..1000',
    [Font.CrimsonText]: 'family=Crimson+Text:ital,wght@0,400;0,700;1,400',
    [Font.IBMPlexMono]: 'family=IBM+Plex+Mono:ital,wght@0,400;0,700;1,400;1,700',
    [Font.GreatVibes]: 'family=Great+Vibes',
    [Font.Cinzel]: 'family=Cinzel:wght@400..900',
    [Font.PlayfairDisplay]: 'family=Playfair+Display:ital,wght@0,400..900;1,400..900',
    [Font.PinyonScript]: 'family=Pinyon+Script',
    [Font.Graduate]: 'family=Graduate',
    [Font.Pacifico]: 'family=Pacifico',
    [Font.LobsterTwo]: 'family=Lobster+Two:ital,wght@0,400;0,700;1,400',
    [Font.Jost]: 'family=Jost:ital,wght@0,100..900;1,100..900',
    [Font.Yellowtail]: 'family=Yellowtail',
    [Font.Sacramento]: 'family=Sacramento',
    // the four faces the INST-PLATES templates use (400/600, upright and italic), as the print PDF bundles them
    [Font.CormorantGaramond]: 'family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400;1,600',
    // the one face the EP-UPLOAD compiler emits for Allura (400 upright), as the print PDF bundles it
    [Font.Allura]: 'family=Allura',
};

const loadedFonts = new Set<Font>();

export const loadEPFont = (fontId: Font): void => {
    if (loadedFonts.has(fontId)) return;

    const param = GOOGLE_FONT_PARAMS[fontId];

    if (!param) return;

    loadedFonts.add(fontId);

    const link = document.createElement('link');

    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?${param}&display=swap`;
    document.head.appendChild(link);
};

const FONT_NAME_TO_ID: Record<string, Font> = {
    roboto: Font.Roboto,
    'times new roman': Font.TimesNewRoman,
    tinos: Font.TimesNewRoman,
    'libre baskerville': Font.LibreBaskerville,
    carlito: Font.Carlito,
    'old standard tt': Font.OldStandardTT,
    arial: Font.Arial,
    'eb garamond': Font.EBGaramond,
    'nunito sans': Font.NunitoSans,
    'crimson text': Font.CrimsonText,
    'ibm plex mono': Font.IBMPlexMono,
    // UV-print decorative fonts — matched by family-name substring so the
    // imagegen renderer (imagegen1-render.tsx) loads them from the resolved
    // uv_lines fontFamily strings.
    'great vibes': Font.GreatVibes,
    cinzel: Font.Cinzel,
    'playfair display': Font.PlayfairDisplay,
    'pinyon script': Font.PinyonScript,
    graduate: Font.Graduate,
    pacifico: Font.Pacifico,
    'lobster two': Font.LobsterTwo,
    jost: Font.Jost,
    yellowtail: Font.Yellowtail,
    sacramento: Font.Sacramento,
    // plate-template-only fonts
    'cormorant garamond': Font.CormorantGaramond,
    allura: Font.Allura,
};

const FONT_TOKEN_TO_ID: Record<string, Font> = {
    roboto: Font.Roboto,
    'libre-baskerville': Font.LibreBaskerville,
    carlito: Font.Carlito,
    'times-new-roman': Font.TimesNewRoman,
    'old-standard-tt': Font.OldStandardTT,
    arial: Font.Arial,
    'eb-garamond': Font.EBGaramond,
    'nunito-sans': Font.NunitoSans,
    'crimson-text': Font.CrimsonText,
    'ibm-plex-mono': Font.IBMPlexMono,
};

export const loadEPFontsFromHTML = (html: string): void => {
    if (!html) return;

    const lower = html.toLowerCase();

    for (const [name, fontId] of Object.entries(FONT_NAME_TO_ID)) {
        if (lower.includes(name)) {
            loadEPFont(fontId as Font);
        }
    }

    for (const [token, fontId] of Object.entries(FONT_TOKEN_TO_ID)) {
        if (lower.includes(`ql-font-${token}`) || lower.includes(`data-font="${token}"`) || lower.includes(`data-font='${token}'`)) {
            loadEPFont(fontId as Font);
        }
    }
};

/**
 * Re-derive each `font-family` from its `data-font` token using the current
 * FONT_FAMILY_MAP. This upgrades already-stored plate HTML (e.g. a cart item
 * saved before the metric-compatible web fonts were added) so it renders with
 * the up-to-date fallback chain — important for the image generator, which would
 * otherwise fall back to a wider serif and wrap text onto an extra line.
 */
export const applyFontFamiliesFromDataFont = (html: string): string => {
    if (!html || typeof document === 'undefined' || !html.includes('data-font')) return html;

    const tmp = document.createElement('div');
    tmp.innerHTML = html;

    tmp.querySelectorAll<HTMLElement>('[data-font]').forEach(el => {
        const token = (el.getAttribute('data-font') || '').toLowerCase();
        const fontId = FONT_TOKEN_TO_ID[token];
        if (fontId && FONT_FAMILY_MAP[fontId]) {
            el.style.fontFamily = FONT_FAMILY_MAP[fontId];
        }
    });

    return tmp.innerHTML;
};

export const parseEngravedPlateSettings = (egs: string, pcs: {[key: string]: any}): void => {
    if (!egs) return;

    const tokens = egs.split(':');

    if (tokens.length < 2) return;

    const fontId = parseInt(tokens[0], 10) as Font;

    engravedPlateExtendedStore.fontFamily = FONT_FAMILY_MAP[fontId] ?? FONT_FAMILY_MAP[Font.Roboto];
    loadEPFont(fontId);

    const lines: EPLine[] = [];

    for (let i = 1; i < tokens.length; i++) {
        const parts = tokens[i].split('-');
        if (parts.length < 4) continue;

        const lineNum = parseInt(parts[0], 10);
        const size = parseInt(parts[1], 10) as Size;
        const bold = parts[2] === '1';
        const italic = parts[3] === '1';
        const text = pcs[`EP_${lineNum}`] || '';

        lines[lineNum - 1] = {text, lineHeight: size, size, bold, italic};
    }

    engravedPlateExtendedStore.lines = lines;
};
